import { Component, EventEmitter, Output } from '@angular/core';
import { CarrierId, SCV2_CARRIERS, SCV2_UPS_ENABLED, ShippingConfigV2Service } from '../../shipping-config-v2.service';

interface AccountStep {
  text: string;
  tag: 'auto' | 'manual' | null;
}

@Component({
  selector: 'app-shipping-carrier-setup-v2',
  templateUrl: './shipping-carrier-setup-v2.component.html',
  styleUrls: ['./shipping-carrier-setup-v2.component.scss'],
})
export class ShippingCarrierSetupV2Component {
  @Output() goToRules = new EventEmitter<void>();

  carriers = SCV2_CARRIERS;
  // USPS-only for first version. UPS card is kept in code, gated behind SCV2_UPS_ENABLED. TODO re-enable UPS.
  carrierIds: CarrierId[] = SCV2_UPS_ENABLED ? ['ups', 'usps'] : ['usps'];

  // collapsible "new to carrier?" guide open-state, keyed by carrier
  guideOpen: { [k: string]: boolean } = { ups: false, usps: false };

  // connect modal state
  modal: { carrier: CarrierId; mode: 'api' | 'manual' } | null = null;
  testState: 'idle' | 'testing' | 'ok' = 'idle';
  form: any = {};

  readonly manualAccountHint: Record<CarrierId, string> = {
    ups: "Sign up free at ups.com with your business address, then ask UPS for your shipping account number — that's all you need.",
    usps: "Sign up free at usps.com — that's all you need. You can ship from any Post Office or print labels on their site.",
  };

  readonly accountSteps: Record<CarrierId, AccountStep[]> = {
    ups: [
      { text: 'Sign up for a free account at ups.com — just like signing up for any website.', tag: null },
      { text: "Ask UPS for your shipping account number. They'll give you one when you sign up, or you can call them.", tag: null },
      { text: "Tell UPS you want to connect an outside app (like LincSell). They'll email you two codes — just copy and paste them in here.", tag: 'auto' },
      { text: "That's it for self-shipping — you'll keep shipping the way you do now and just tell us the tracking number after.", tag: 'manual' },
    ],
    usps: [
      { text: 'Sign up for a free account at usps.com — just like signing up for any website.', tag: null },
      { text: "Ask USPS to connect an outside app (like LincSell) to your account. They'll email you two codes — just copy and paste them in here.", tag: 'auto' },
      { text: "That's it for self-shipping — you can ship from any Post Office or print labels on their site, then tell us the tracking number after.", tag: 'manual' },
    ],
  };

  constructor(public service: ShippingConfigV2Service) {}

  get connections() {
    return this.service.connections;
  }

  get anyConnected(): boolean {
    return !!(this.connections.ups || this.connections.usps);
  }

  developerUrl(id: CarrierId): string {
    return id === 'ups' ? 'https://developer.ups.com' : 'https://developer.usps.com';
  }

  toggleGuide(id: CarrierId): void {
    this.guideOpen[id] = !this.guideOpen[id];
  }

  openModal(carrier: CarrierId, mode: 'api' | 'manual'): void {
    this.form = {};
    this.testState = 'idle';
    this.modal = { carrier, mode };
  }

  closeModal(): void {
    this.modal = null;
  }

  testConnection(): void {
    this.testState = 'testing';
    setTimeout(() => (this.testState = 'ok'), 900);
  }

  saveConnection(): void {
    if (!this.modal) {
      return;
    }
    this.service.connections[this.modal.carrier] = {
      mode: this.modal.mode,
      status: 'connected',
      ...this.form,
    };
    this.modal = null;
  }

  disconnect(id: CarrierId): void {
    this.service.connections[id] = null;
  }
}
